<template>
  <div>
    <div v-if="!showAddUser && hasPermission('Add User')" class="btn-group-hdr">
      <div class="btn-group btn-group-toggle">
        <!--<router-link :to="create" class="btn btn-primary btn-sm">Add User</router-link>-->
        <button class="btn btn-primary btn-sm" @click="addForm()">Add User</button>
      </div>
    </div>

    <div class="title-hdr bg-dark">
      {{title}}
    </div>

    <!-- AddEditUser component -->
    <add-edit-user
      v-if="showAddUser"
      :user="editingUser"
      @cancel="cancelEdit"
      @user-added="onUserAdded"
      @user-updated="onUserUpdated"
    ></add-edit-user>

    <manage-roles
        v-if="showManageRoles"
        :user="selectedUser"
        :initial-left="manageRolesState.left"
        :initial-right="manageRolesState.right"
        :loading="manageRolesState.loading"
        :saving="manageRolesState.saving"
        :error-text="manageRolesState.error"
        @close="closeManageRoles"
        @save="saveUserRoles"
        :has-permission="hasPermission"
    />

    <!-- AssignUserPermissions component -->
    <assign-user-permissions
        v-if="showAssignUserPermissions"
        :user="userForPermissions"
        :permissions="userPermissions"
        :role-permissions="rolePermissions"
        @cancel="cancelAssignUserPermissions"
        @save="saveAssignedUserPermissions"
        :has-permission="hasPermission"
    />

    <div class="card" v-if="!showAddUser && !showAssignUserPermissions && !showManageRoles">
      <div class="card-body">
        <data-viewer :source="source" :thead="thead" ref="dataViewer" :key="dataViewerKey">
          <template slot-scope="props">
            <tr>
              <td>{{ props.item.name }}</td>
              <td>{{ props.item.username }}</td>
              <!-- Activation -->
              <td v-if="hasPermission('Activate User')">
                <div class="toggle-wrapper">
                  <label
                      class="switch switch--active"
                      role="switch"
                      :aria-checked="props.item.is_active === 1 ? 'true' : 'false'"
                      aria-label="Toggle Activation"
                      @click.stop.prevent="confirmToggleActive(props.item)"
                  >
                    <input
                        type="checkbox"
                        :id="`active-${props.item.user_id}`"
                        :checked="props.item.is_active === 1"
                        tabindex="-1"
                    />
                    <span class="slider"></span>
                  </label>
                </div>
              </td>
              <!-- Lock -->
              <td v-if="hasPermission('Lock User')">
                <div class="toggle-wrapper">
                  <label
                      class="switch switch--lock"
                      role="switch"
                      :aria-checked="props.item.is_locked === 1 ? 'true' : 'false'"
                      aria-label="Toggle Lock"
                      @click.stop.prevent="confirmToggleLock(props.item)"
                  >
                    <input
                        type="checkbox"
                        :id="`lock-${props.item.user_id}`"
                        :checked="props.item.is_locked === 1"
                        tabindex="-1"
                    />
                    <span class="slider"></span>
                  </label>
                </div>
              </td>
              <td>
                <button v-if="hasPermission('Edit User')" class="btn btn-primary btn-sm" data-bs-toggle="tooltip" title="Edit User" @click="openEditUserForm(props.item)">
                  <i class="fas fa-pen fa-sm"></i>
                </button>
                <button v-if="hasPermission('Assign User Roles')" class="btn btn-info btn-sm" data-bs-toggle="tooltip" title="Manage Roles" @click="openManageRoles(props.item)">
                  <i class="fas fa-users-cog fa-sm"></i>
                </button>
                <button v-if="hasPermission('Assign User Management Permissions')||hasPermission('Assign DRP Permissions')||hasPermission('Assign Store Permissions')" class="btn btn-secondary btn-sm ms-1"
                        data-bs-toggle="tooltip" title="Assign Permission" @click="assignUserPermissions(props.item)">
                  <i class="fas fa-key fa-sm"></i> <!-- key icon for permissions -->
                </button>
                <!--<button class="btn btn-danger btn-sm ms-1" data-bs-toggle="tooltip" title="Delete User" @click="deleteUser(props.item)">
                  <i class="fas fa-trash fa-sm"></i>
                </button>-->
              </td>
            </tr>
          </template>
        </data-viewer>
      </div>
    </div>
  </div>
</template>

<script>
  import axios from 'axios'
  import { baseUrl } from './../../../.env.js'
  import { bus } from '../../../main.js'
  import AddEditUser from './AddEditUser.vue'
  import DataViewer from './../common/DataViewer.vue'
  import ManageRoles from './ManageRoles.vue'
  import AssignUserPermissions from './AssignUserPermissions.vue'

  export default {
    name: 'UserData',
    props: {
      hasPermission: {
        type: Function,
        required: true
      }
    },
    components: {
      DataViewer,
      AddEditUser,
      ManageRoles,
      AssignUserPermissions  // Register the component
    },
    data() {
      return {
        certs: {},
        baseUrl : baseUrl,
        title: 'Users',
        source: '/user/sys/users/paginate',

        // ManageRoles view state
        showManageRoles: false,
        selectedUser: null,
        manageRolesState: {
          loading: false,
          saving: false,
          error: '',
          left: [],   // user_roles
          right: [],  // user_manage_roles MINUS already-left
        },

        // Add/Edit user
        showAddUser: false,
        editingUser: null,

        // New state for assign user permissions
        showAssignUserPermissions: false,
        userForPermissions: null,
        userPermissions: [],
        rolePermissions: [],  // fix typo from rolePermission to rolePermissions
        dataViewerKey: 0,
      }
    },
    mounted() {
      this.$store.commit('selectedTab', 'users');

      // existing tooltip init...
      var tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'))
      tooltipTriggerList.forEach(function (tooltipTriggerEl) {
        new bootstrap.Tooltip(tooltipTriggerEl)
      });

      // Listen for "go back to users root page"
      bus.$on('nav:users-root', this.goToUsersRoot);
    },
    beforeDestroy() {
      bus.$off('nav:users-root', this.goToUsersRoot);
    },
    methods: {
      isUserActivated(user) {
        return Number(user && user.is_active) === 1;
      },
      isUserLocked(user) {
        return Number(user && user.is_locked) === 1;
      },
      async confirmToggleActive(user) {
        const currentlyActivated = this.isUserActivated(user);
        const title = currentlyActivated ? 'Deactivate User' : 'Activate User';
        const text  = currentlyActivated
            ? 'Do you want to deactivate this User?'
            : 'Do you want to Activate this User?';

        const res = await Swal.fire({
          title: title,
          text: text,
          type: 'warning',                         // v7/8 uses `type`, not `icon`
          showCancelButton: true,
          confirmButtonText: currentlyActivated ? 'Yes, deactivate' : 'Yes, activate',
          cancelButtonText: 'Cancel'
        });

        // v7/8: confirm => res.value === true
        if (!res || !res.value) return;

        try {
          const url = '/user/sys/user/' + user.user_id + '/status';
          const payload = { is_active: currentlyActivated ? 0 : 1 };
          const response = await axios.put(url, payload);

          const msg = (response && response.data && response.data.message)
              ? response.data.message
              : (currentlyActivated ? 'User has been deactivated.' : 'User has been activated.');

          await Swal.fire({ type: 'success', title: 'Success', text: msg });
          this.refreshData();
        } catch (err) {
          let message = 'Failed to update user activation.';
          if (err && err.response && err.response.data) {
            if (typeof err.response.data === 'string') message = err.response.data;
            else if (err.response.data.error) message = err.response.data.error;
            else if (err.response.data.message) message = err.response.data.message;
          }
          Swal.fire({ type: 'error', title: 'Error', text: message });
        }
      },
      async confirmToggleLock(user) {
        const currentlyLocked = this.isUserLocked(user);
        const title = currentlyLocked ? 'Unlock User' : 'Lock User';
        const text  = currentlyLocked
            ? 'Do you want to unlock this User?'
            : 'Do you want to lock this User?';

        const res = await Swal.fire({
          title: title,
          text: text,
          type: 'warning',                         // v7/8 uses `type`, not `icon`
          showCancelButton: true,
          confirmButtonText: currentlyLocked ? 'Yes, unlock' : 'Yes, lock',
          cancelButtonText: 'Cancel'
        });

        // v7/8: confirm => res.value === true
        if (!res || !res.value) return;

        try {
          const url = '/user/sys/user/' + user.user_id + '/lock';
          const payload = { is_locked: currentlyLocked ? 0 : 1 };
          const response = await axios.put(url, payload);

          const msg = (response && response.data && response.data.message)
              ? response.data.message
              : (currentlyLocked ? 'User has been unlocked.' : 'User has been locked.');

          await Swal.fire({ type: 'success', title: 'Success', text: msg });
          this.refreshData();
        } catch (err) {
          let message = 'Failed to update user lock status.';
          if (err && err.response && err.response.data) {
            if (typeof err.response.data === 'string') message = err.response.data;
            else if (err.response.data.error) message = err.response.data.error;
            else if (err.response.data.message) message = err.response.data.message;
          }
          Swal.fire({ type: 'error', title: 'Error', text: message });
        }
      },
      goToUsersRoot() {
        // Close any sub-views
        this.showAddUser = false;
        this.editingUser = null;

        this.showAssignUserPermissions = false;
        this.userForPermissions = null;
        this.userPermissions = [];
        this.rolePermissions = [];

        this.showManageRoles = false;
        this.activeUser = null;
        this.manageRolesState = {
          loading: false,
          saving: false,
          error: '',
          left: [],
          right: [],
        };

        // Optional: refresh table
        this.refreshData && this.refreshData();
      },
      async openManageRoles(user) {
        this.selectedUser = user
        this.showManageRoles = true;             // switch the page
        this.manageRolesState.loading = true;    // show spinner in ManageRoles page
        this.manageRolesState.error = '';
        this.manageRolesState.left = [];
        this.manageRolesState.right = [];

        try {
          const url = `/user/sys/user/${user.user_id}/manage/roles`;
          const resp = await axios.get(url);
          const data = resp && resp.data ? resp.data : null;

          if (!data || data.code !== 200 || !data.model) {
            throw new Error('Unexpected response');
          }

          const model = data.model || {};
          const left = Array.isArray(model.user_roles) ? model.user_roles : [];
          const rightAll = Array.isArray(model.user_manage_roles) ? model.user_manage_roles : [];

          // exclude already-assigned roles from right
          const leftIds = {};
          for (var i = 0; i < left.length; i++) leftIds[left[i].role_id] = true;

          const right = [];
          for (var j = 0; j < rightAll.length; j++) {
            var r = rightAll[j];
            if (!leftIds[r.role_id]) right.push(r);
          }

          this.manageRolesState.left = left;
          this.manageRolesState.right = right;
        } catch (err) {
          console.error(err);
          this.manageRolesState.error = 'Failed to load roles. Please try again.';
          this.manageRolesState.left = [];
          this.manageRolesState.right = [];
        } finally {
          this.manageRolesState.loading = false;
        }
      },
      closeManageRoles() {
        this.editingUser = null
        this.showManageRoles = false;
        this.manageRolesState = {
          loading: false,
          saving: false,
          error: '',
          left: [],
          right: [],
        };
      },
      async saveUserRoles(user_id, roleIds) {
        this.manageRolesState.saving = true;
        console.log("stif:user | ", user_id, user_id)
        try {
          // Adjust to your real save endpoint if different:
          const url = `/user/sys/user/${user_id}/manage/roles`;
          await axios.put(url, { data: roleIds });

          // Optional: refresh list
          this.refreshData();

          // Back to users list
          this.closeManageRoles();

          // Optional: toast
          Swal && Swal.fire && Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'User roles saved successfully!'
          });
        } catch (e) {
          console.error(e);
          this.manageRolesState.saving = false;
          Swal && Swal.fire && Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to save user roles.'
          });
        }
        this.editingUser = null
      },
      addForm() {
        this.editingUser = null
        this.showAddUser = true
      },
      openEditUserForm(user) {
        this.editingUser = user
        this.showAddUser = true
      },
      cancelEdit() {
        this.showAddUser = false
        this.editingUser = null
      },
      onUserAdded() {
        this.showAddUser = false
        this.editingUser = null
        this.refreshData()
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'User added successfully!'
        })
      },
      onUserUpdated() {
        this.showAddUser = false
        this.editingUser = null
        this.refreshData()
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'User updated successfully!'
        })
      },
      refreshData() {
        // If DataViewer exposes a reload(), call it
        if (this.$refs.dataViewer && this.$refs.dataViewer.reload) {
          this.$refs.dataViewer.reload()
        }
        // Also force a re-render so any internal caching is bypassed
        this.dataViewerKey += 1
      },
      async deleteUser(user) {
        const result = await Swal.fire({
          title: 'Are you sure?',
          text: `Do you want to delete user "${user.name}"? This action cannot be undone.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          cancelButtonColor: '#3085d6',
          confirmButtonText: 'Yes, delete it!'
        })

        if (result.isConfirmed) {
          try {
            await axios.delete(`${this.baseUrl}/user/sys/users/${user.id}`)
            Swal.fire({
              icon: 'success',
              title: 'Deleted!',
              text: 'User has been deleted.'
            })
            this.refreshData()
          } catch (error) {
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: (error.response && error.response.data && error.response.data.message) || 'Failed to delete user.'
            })
          }
        }
      },

      // New method to open assign user permissions UI
      async assignUserPermissions(user) {
        // Fetch user permissions
        const url = `/user/sys/${user.user_id}/permissions`
        await axios.get(url)
            .then(response => {
              if (response.data && response.data.permissions) {
                this.userPermissions = response.data.permissions
              } else {
                this.userPermissions = []
              }
              this.userForPermissions = Object.assign({}, user)

              // Fetch role permissions for this user
              // if (user.role_id) {
              //   this.assignRolePermissions(user)
              // } else {
              //   this.rolePermissions = []
              //   this.showAssignUserPermissions = true
              // }
            })
            .catch(error => {
              this.userPermissions = []
              Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load user permissions.'
              })
            })

        // Fetch user role permissions for this user
        this.assignRolePermissions(user)
      },
      async assignRolePermissions(user) {
        const url = `/user/sys/user/${user.user_id}/role/permissions`
        await axios.get(url)
          .then((response) => {
            if (response.data && response.data.permissions) {
              this.rolePermissions = response.data.permissions
            } else {
              this.rolePermissions = []
            }
            this.showAssignUserPermissions = true
          })
          .catch((error) => {
            this.rolePermissions = []
            this.showAssignUserPermissions = true
          })
      },
      cancelAssignUserPermissions() {
        this.userForPermissions = null
        this.userPermissions = []
        this.showAssignUserPermissions = false
      },
      saveAssignedUserPermissions({userId, permissions}) {
        if (!userId) {
          console.error('userId is undefined!')
          return
        }
        const url = `/user/sys/${userId}/permissions`
        axios.post(url, {permissions})
            .then(response => {
              const message = (response.data && response.data.message) || 'User permissions saved successfully!'
              Swal.fire({
                icon: 'success',
                title: 'Success',
                text: message
              }).then(() => {
                this.showAssignUserPermissions = false
                this.userForPermissions = null
                this.userPermissions = []
                this.refreshData()
              })
            })
            .catch(err => {
              let message = 'Failed to save permissions'
              if (err && err.response && err.response.data) {
                if (typeof err.response.data === 'string') {
                  message = err.response.data
                } else if (err.response.data.error) {
                  message = err.response.data.error
                } else if (err.response.data.message) {
                  message = err.response.data.message
                }
              }
              Swal.fire({
                icon: 'error',
                title: 'Error',
                text: message
              })
              console.error(err)
            })
      }
    },
    computed: {
      thead() {
        const cols = [
          { title: 'Name', key: 'name', sort: true },
          { title: 'Username', key: 'username', sort: true }
        ];
        if (this.hasPermission('Activate User')) {
          cols.push({ title: 'Status', key: 'status' });
        }
        if (this.hasPermission('Lock User')) {
          cols.push({ title: 'Access Lock', key: 'access_lock' });
        }
        return cols;
      },
    }
  }
</script>

<style>
/* Container */
.toggle-wrapper {
  display: inline-flex;
  align-items: center;
}

/* Base switch with CSS variables */
.switch {
  --switch-w: 100px;   /* width of the track (make this bigger for longer words) */
  --switch-h: 30px;    /* height of the track */
  --thumb: 24px;       /* size of the circle */
  --pad-x: 12px;       /* inner horizontal padding for the label text */
  --off-bg: #ccc;
  --on-bg: #4CAF50;    /* default ON color (Activation) */

  position: relative;
  display: inline-block;
  width: var(--switch-w);
  height: var(--switch-h);
  cursor: pointer;
}

.switch input { display: none; }

/* Track */
.slider {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: var(--off-bg);
  border-radius: 30px;
  transition: 0.4s;
  font-size: 12px;
  font-weight: bold;
  color: #fff;
  display: flex;
  align-items: center;
  padding: 0 var(--pad-x);
  /* Start with OFF text aligned to the right so the thumb has room on left */
  justify-content: flex-end;
}

/* Thumb (circle) */
.slider::before {
  content: "";
  position: absolute;
  height: var(--thumb);
  width: var(--thumb);
  left: 1px;
  bottom: calc((var(--switch-h) - var(--thumb)) / 2);
  background-color: #fff;
  border-radius: 50%;
  transition: 0.4s;
}

/* Default label (will be overridden per type below) */
.slider::after {
  content: "OFF";
  white-space: nowrap;              /* prevents wrapping */
  pointer-events: none;
}

/* Checked visuals */
.switch input:checked + .slider {
  background-color: var(--on-bg);
  justify-content: flex-start;      /* ON text on left side */
}
.switch input:checked + .slider::before {
  /* Move the thumb exactly across the track minus paddings */
  transform: translateX(calc((var(--switch-w) - var(--thumb) - var(--pad-x)) + 6px));
  /* 6px = left(3) + right(3) visual gap; tweak if your borders/gaps differ */
}

/* === Activation text (Deactivated / Activate) === */
.switch--active .slider::after {
  content: "Deactivated";
}
.switch--active input:checked + .slider::after {
  content: "Activated";
}

/* === Lock text (Unlocked / Locked) === */
.switch--lock .slider::after {
  content: "Unlocked";
}
/* Lock ON uses a red track to differentiate from Activation */
.switch--lock {
  --on-bg: #dc3545;  /* Bootstrap 'danger' */
}
.switch--lock input:checked + .slider::after {
  content: "Locked";
}

/* Optional: if Activation words still feel tight, make just that switch wider */
.switch--active {
  --switch-w: 110px;  /* a bit wider for 'Deactivated' */
}

/* Optional: if you want Lock tighter because words are shorter */
.switch--lock {
  --switch-w: 100px;
}
</style>



// WEBPACK FOOTER //
// src/components/users/users/UserData.vue